import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AnimatedIcon from '../method/AnimatedIcon';
import SectionHeading from '../home/SectionHeading';
import { useInView, reveal } from '../home/motion';
import { useT } from '../../i18n/LanguageContext';

// The five tools the Pro profile unlocks, each with room to breathe. The
// visual column is a card-family SVG (src/assets/method-icons) drawn
// stroke-by-stroke and filled on a loop — the animation IS the focal point,
// so the heading stays plain text with no inline icon. The sixth value
// prop, bespoke decks, lives in its own section (PlatformCustomCards).
function getDeliverables(t) {
  return [
    {
      icon: 'aihead',
      name: t('platform.deliverables.item1Name'),
      text: t('platform.deliverables.item1Text'),
    },
    {
      icon: 'machine',
      name: t('platform.deliverables.item2Name'),
      text: t('platform.deliverables.item2Text'),
    },
    {
      icon: 'network',
      name: t('platform.deliverables.item3Name'),
      text: t('platform.deliverables.item3Text'),
    },
    {
      icon: 'collab',
      name: t('platform.deliverables.item4Name'),
      text: t('platform.deliverables.item4Text'),
    },
    {
      icon: 'robot',
      name: t('platform.deliverables.item5Name'),
      text: t('platform.deliverables.item5Text'),
    },
  ];
}

function Row({ item, i }) {
  const theme = useTheme();
  const { ref, visible } = useInView();
  const flip = i % 2 === 1;
  const accent = flip ? theme.home.coralText : theme.home.cyanText;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 3, md: 7 }, alignItems: 'center', ...reveal(visible, 0.06),
      }}
    >
      <Box sx={{ order: { xs: 2, md: flip ? 2 : 1 } }}>
        <Typography sx={{
          fontFamily: theme.home.display, fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.7rem' },
          color: theme.home.ink, mb: 1.5,
        }}>
          {item.name}
        </Typography>
        <Typography sx={{ color: theme.home.inkMuted, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 480 }}>
          {item.text}
        </Typography>
      </Box>

      <Box
        sx={{
          order: { xs: 1, md: flip ? 1 : 2 }, minHeight: { xs: 200, md: 280 }, borderRadius: 5,
          background: `linear-gradient(135deg, ${theme.home.cyan}14, ${theme.home.coral}14)`,
          border: `1px solid ${theme.home.paperDeep}`, display: 'grid', placeItems: 'center', p: 3,
        }}
      >
        <AnimatedIcon name={item.icon} color={accent} size={200} startDelayMs={i * 450} />
      </Box>
    </Box>
  );
}

export default function PlatformDeliverables() {
  const theme = useTheme();
  const t = useT();
  const DELIVERABLES = getDeliverables(t);

  return (
    <Box sx={{ bgcolor: theme.home.paper, py: theme.home.sectionPy }}>
      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 } }}>
        <SectionHeading
          eyebrow={t('platform.deliverables.eyebrow')}
          title={t('platform.deliverables.title')}
          lede={t('platform.deliverables.lede')}
        />
        <Box sx={{ mt: { xs: 6, md: 9 }, display: 'flex', flexDirection: 'column', gap: { xs: 7, md: 12 } }}>
          {DELIVERABLES.map((d, i) => <Row key={d.name} item={d} i={i} />)}
        </Box>
      </Box>
    </Box>
  );
}
